class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getRecentSearches() {
    try {
      const raw = localStorage.getItem('mgp_recent_searches');
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.slice(0, 5) : [];
    } catch (e) {
      return [];
    }
  }

  saveRecentSearch(term) {
    const clean = (term || '').trim();
    if (!clean || clean.length < 2) return;
    try {
      const prev = this.getRecentSearches().filter((item) => item.toLowerCase() !== clean.toLowerCase());
      prev.unshift(clean);
      localStorage.setItem('mgp_recent_searches', JSON.stringify(prev.slice(0, 5)));
    } catch (e) {}
  }

  escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[ch]));
  }

  renderZeroQueryPanel() {
    const recent = this.getRecentSearches();
    const popular = [
      { label: 'Hi-Vis Polo', url: '/search?q=Hi-Vis+Polo&options%5Bprefix%5D=last&type=product' },
      { label: 'Safety Boots', url: '/search?q=Safety+Boots&options%5Bprefix%5D=last&type=product' },
      { label: 'Cut 5 Gloves', url: '/search?q=Cut+5+Gloves&options%5Bprefix%5D=last&type=product' },
      { label: 'Flame Resistant', url: '/search?q=Flame+Resistant&options%5Bprefix%5D=last&type=product' },
      { label: 'Wet Weather', url: '/search?q=Wet+Weather&options%5Bprefix%5D=last&type=product' },
    ];
    const categories = [
      { label: 'Workwear', url: '/collections/workwear' },
      { label: 'Safety Boots', url: '/collections/safety-boots' },
      { label: 'PPE & Safety', url: '/collections/ppe' },
      { label: 'Corporate Uniforms', url: '/collections/corporate-uniforms' },
      { label: 'Healthcare & Scrubs', url: '/collections/healthcare-scrubs' },
      { label: 'Safety & Site Equipment', url: '/collections/safety-site-equipment' },
    ];
    const brands = [
      { label: 'Hard Yakka', url: '/collections/hard-yakka' },
      { label: 'Bisley', url: '/collections/bisley' },
      { label: "JB's Wear", url: '/collections/jb-wear' },
      { label: 'Steel Blue', url: '/collections/steel-blue' },
      { label: 'Force360', url: '/collections/force360' },
      { label: 'Portwest', url: '/collections/portwest' },
    ];

    const recentHtml = recent.length
      ? `<div class="mgp-zq__section" data-zq-recent>
          <div class="mgp-zq__header">
            <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Recent Searches</h2>
            <button type="button" class="mgp-zq__clear link" data-zq-clear-recent>Clear</button>
          </div>
          <div class="mgp-zq__chips" role="group" aria-label="Recent Searches">
            ${recent.map((term, i) => `<a id="predictive-search-option-recent-${i}" role="option" aria-selected="false" class="mgp-zq__chip" href="/search?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last&type=product">${this.escapeHtml(term)}</a>`).join('')}
          </div>
        </div>`
      : '';

    const markup = `
      <style>
        .mgp-zq { padding: 16px 20px; text-align: left; color: #f3f3f3; }
        .mgp-zq__section { margin-bottom: 14px; }
        .mgp-zq__section:last-child { margin-bottom: 0; }
        .mgp-zq__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
        .mgp-zq .predictive-search__heading { margin: 0 0 8px 0; padding: 0; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--mg-hi-vis, #facb00); }
        .mgp-zq__header .predictive-search__heading { margin-bottom: 0; }
        .mgp-zq__clear { background: none; border: 0; padding: 0; font-size: 12px; color: #f3f3f3; cursor: pointer; text-decoration: underline; }
        .mgp-zq__chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .mgp-zq__chip { display: inline-flex; align-items: center; padding: 6px 12px; font-size: 13px; font-weight: 500; line-height: 1.2; color: #141414; background: #f5f5f5; border: 1px solid rgba(20,20,20,0.14); border-radius: 999px; text-decoration: none; transition: background 0.15s ease, border-color 0.15s ease; }
        .mgp-zq__chip:hover, .mgp-zq__chip[aria-selected="true"] { background: var(--mg-hi-vis, #facb00); border-color: #141414; color: #141414; }
        .mgp-zq__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
        @media screen and (max-width: 749px) { .mgp-zq__grid { grid-template-columns: 1fr; } }
      </style>
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="mgp-zq" data-zero-query-panel>
          ${recentHtml}
          <div class="mgp-zq__section" data-zq-popular>
            <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Popular Trade Searches</h2>
            <div class="mgp-zq__chips" role="group" aria-label="Popular Trade Searches">
              ${popular.map((item, i) => `<a id="predictive-search-option-popular-${i}" role="option" aria-selected="false" class="mgp-zq__chip" data-zq-term="${this.escapeHtml(item.label)}" href="${item.url}">${this.escapeHtml(item.label)}</a>`).join('')}
            </div>
          </div>
          <div class="mgp-zq__grid">
            <div class="mgp-zq__section" data-zq-categories>
              <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Top Categories</h2>
              <div class="mgp-zq__chips" role="group" aria-label="Top Categories">
                ${categories.map((item, i) => `<a id="predictive-search-option-cat-${i}" role="option" aria-selected="false" class="mgp-zq__chip" href="${item.url}">${this.escapeHtml(item.label)}</a>`).join('')}
              </div>
            </div>
            <div class="mgp-zq__section" data-zq-brands>
              <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Top Brands</h2>
              <div class="mgp-zq__chips" role="group" aria-label="Top Brands">
                ${brands.map((item, i) => `<a id="predictive-search-option-brand-${i}" role="option" aria-selected="false" class="mgp-zq__chip" href="${item.url}">${this.escapeHtml(item.label)}</a>`).join('')}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    const clearBtn = this.predictiveSearchResults.querySelector('[data-zq-clear-recent]');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        try { localStorage.removeItem('mgp_recent_searches'); } catch (err) {}
        this.renderZeroQueryPanel();
        this.input.focus();
      });
    }
    this.predictiveSearchResults.querySelectorAll('[data-zq-term]').forEach((link) => {
      link.addEventListener('click', () => {
        this.saveRecentSearch(link.getAttribute('data-zq-term'));
      });
    });
    this.open();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.renderZeroQueryPanel();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    this.saveRecentSearch(this.getQuery());
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.renderZeroQueryPanel();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.renderZeroQueryPanel();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length && event.code !== 'Escape' && event.code !== 'ArrowUp' && event.code !== 'ArrowDown' && event.code !== 'Enter') {
      this.renderZeroQueryPanel();
    }
    event.preventDefault();

    switch (event.code) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources%5Btype%5D=product,collection,query&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
